import { Task, TaskGroupBy } from "@common/types/task";
import { TaskListQuery } from "@common/contracts/task.contract";
import { FiltersTypes } from "@common/types/filters/general";
import { FiltersChangeFn } from "@components/pageHeader/filters/lib";
import { type DataGridHandle, ExpandAllButton } from "@design-system/DataGrid";
import { ViewToolbar } from "@components/views/ViewToolbar/ViewToolbar";
import { useAssignedToMeViewLabels } from "@components/views/useAssignedToMeViewLabels";
import { ASSIGNED_TO_ME_VIEW_ID } from "./views/OrganizationTaskViews";
import { useTaskViewCollection } from "./views/useTaskViewCollection";
import { useTaskViewEmptyState } from "./views/useTaskViewEmptyState";
import {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { DEFAULT_TASK_FILTERS } from "./filters/lib";
import { DEFAULT_TASK_SORT } from "./taskListConstants";
import { TaskListOpenTaskContext } from "./taskList.context";
import {
    OrganizationTasksListBulkActions,
    WorkspaceTasksListBulkActions,
} from "./TasksListBulkActions";
import { useTaskAvailableFilters } from "./useTaskAvailableFilters";
import type { TaskWorkspaceData } from "./useTaskWorkspaceData";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import { useGroupedTasks } from "./useGroupedTasks";
import { useTaskGroupsExpansion } from "./useTaskGroupsExpansion";
import { useTaskViewState } from "./useTaskViewState";
import { TasksListHeader } from "./TasksListHeader";
import { NestedTaskRow } from "./types";
import {
    getTaskListGroupBy,
    TaskListCapabilities,
} from "./taskListCapabilities";
import { TasksBoardContent } from "./board/TasksBoardContent";
import type { TaskBoardTask } from "./types";
import { useTasksBoardLayout } from "./board/useTasksBoardLayout";
import { TaskListCacheKeys } from "./taskListCacheKeys";
import { useTasksContext } from "./tasks.context";
import { useHideableSubNavigationToolbar } from "./useHideableSubNavigationToolbar";
import { TaskListTable } from "./taskList/TaskListTable";
import { useTaskListView } from "./taskList/useTaskListView";

const NO_TASKS: Task[] = [];
const NO_ROWS: NestedTaskRow[] = [];

interface Props {
    filters: TaskListQuery;
    setFilters: (
        filters: TaskListQuery | ((prev: TaskListQuery) => TaskListQuery),
    ) => void;
    tasks: TaskBoardTask[] | null;
    /** True while `tasks` are the previous query's, kept on screen until the new ones load. */
    isShowingPreviousTasks: boolean;
    /** True when any tasks failed to load. */
    hasTaskLoadError: boolean;
    /** Set when no tasks loaded because the request failed, for the list's Retry. */
    taskLoadError?: { onRetry: () => void };
    onNavigationTaskIdsChange: (taskIds: string[]) => void;
    overlayFilters: FiltersTypes;
    onOverlayFiltersChange: FiltersChangeFn;
    workspaceData: TaskWorkspaceData;
    capabilities: TaskListCapabilities;
    cacheKeys: TaskListCacheKeys;
    tableNotice?: ReactNode;
    landingViewId?: string;
}

export const TasksList = ({
    filters: requestedFilters,
    setFilters,
    tasks,
    isShowingPreviousTasks,
    hasTaskLoadError,
    taskLoadError,
    onNavigationTaskIdsChange,
    overlayFilters,
    onOverlayFiltersChange,
    workspaceData,
    capabilities,
    cacheKeys,
    tableNotice,
    landingViewId,
}: Props) => {
    const { selectedTaskId, openTask: openTaskInView } = useTasksContext();
    const loadedTasks = tasks ?? NO_TASKS;
    const groupBy = getTaskListGroupBy({
        groupBy: requestedFilters.groupBy,
        capabilities,
    });
    const filters = useMemo(
        () =>
            (requestedFilters.groupBy ?? TaskGroupBy.STATUS) === groupBy
                ? requestedFilters
                : { ...requestedFilters, groupBy },
        [requestedFilters, groupBy],
    );
    const sort = filters.sort ?? DEFAULT_TASK_SORT;
    const isGroupedByCloseStage = groupBy === TaskGroupBy.CLOSE_STAGE;

    const {
        workspaceMemberIds,
        workspaceMembersMap,
        workspaceTaskGroupIds,
        workspaceTaskGroupsMap,
        closeStageIds,
        closeStagesMap,
        closeIds,
        closesMap,
        entitiesNameMap,
        hasCloseStages,
        hasCloses,
        areClosesFetched,
        areClosesFetching,
    } = workspaceData;

    const {
        view,
        fullscreenViewForToolbar,
        handleFullscreenViewChange,
        handleViewSelect,
        effectiveTaskViewConfig,
        builtInTaskViews,
        isLoadingBuiltInTaskViews,
    } = useTaskViewState({
        filters,
        setFilters,
        overlayFilters,
        onOverlayFiltersChange,
        hasCloseStages,
        hasCloses,
        capabilities,
        cacheKeys,
        landingViewId,
    });
    const viewLabels = useAssignedToMeViewLabels(ASSIGNED_TO_ME_VIEW_ID);
    const viewCollection = useTaskViewCollection({
        builtInTaskViews,
        isLoadingBuiltInTaskViews,
        cacheKeys,
    });
    const viewEmptyState = useTaskViewEmptyState({
        tasks,
        isShowingPreviousTasks,
        hasTaskLoadError,
        view: fullscreenViewForToolbar,
        search: filters.search,
        viewCollection,
        builtInViews: builtInTaskViews,
        onViewSelect: handleViewSelect,
    });

    const { groupedTasks } = useGroupedTasks({
        tasks: loadedTasks,
        groupBy,
        isGroupedByCloseStage,
        filters,
        workspaceMemberIds,
        workspaceMembersMap,
        workspaceTaskGroupIds,
        workspaceTaskGroupsMap,
        closeStageIds,
        closeStagesMap,
        closeIds,
        closesMap,
        entitiesNameMap,
        canAddPlaceholders: capabilities.canCreateTasks,
    });
    const tableRows = viewEmptyState ? NO_ROWS : groupedTasks;

    const tableRef = useRef<DataGridHandle>(null);
    const { expanded, setExpanded } = useTaskGroupsExpansion(groupedTasks);

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const clearSelection = useCallback(() => setRowSelection({}), []);

    const setSelectedTaskIds = useCallback(
        (taskIds: string[]) =>
            setRowSelection(
                Object.fromEntries(taskIds.map((taskId) => [taskId, true])),
            ),
        [],
    );

    const { isBoardLayout, board, toolbarControls, clearFilters } =
        useTasksBoardLayout({
            capabilities,
            tasks,
            filters,
            workspaceData,
            setFilters,
            onOverlayFiltersChange,
            onLayoutChange: clearSelection,
            view: fullscreenViewForToolbar,
            onViewChange: handleFullscreenViewChange,
            columnConfigs: effectiveTaskViewConfig.columns,
            viewId: viewCollection.baseViewId,
        });

    const serviceWorkspaceIds = useMemo(
        () =>
            new Set(
                [...closesMap.values()].flatMap(({ workspaceId }) =>
                    workspaceId ? [workspaceId] : [],
                ),
            ),
        [closesMap],
    );
    const { listView, handleListViewChange, emptyState } = useTaskListView({
        view,
        savedView: fullscreenViewForToolbar,
        onSavedViewChange: handleFullscreenViewChange,
        baseView: viewCollection.baseView,
        onViewSelect: handleViewSelect,
        search: filters.search,
        setFilters,
        viewEmptyState,
    });

    const openTask = useCallback(
        (task: Task) =>
            openTaskInView({ taskId: task.id, workspaceId: task.workspaceId }),
        [openTaskInView],
    );

    const openTaskRow = useCallback(
        (task: NestedTaskRow) =>
            openTaskInView({ taskId: task.id, workspaceId: task.workspaceId }),
        [openTaskInView],
    );

    const changeSort = useCallback(
        (value: typeof sort) =>
            setFilters((prev) => ({ ...prev, sort: value })),
        [setFilters],
    );

    const selectedTaskIds = useMemo(() => {
        const selectedRowIds = new Set(
            Object.entries(rowSelection)
                .filter(([, isSelected]) => isSelected)
                .map(([rowId]) => rowId),
        );
        return groupedTasks.flatMap((group: NestedTaskRow) =>
            group.tasks
                .filter(
                    (task) =>
                        !task.isGroup &&
                        !task.isNewPlaceholder &&
                        selectedRowIds.has(task.id),
                )
                .map((task) => task.id),
        );
    }, [groupedTasks, rowSelection]);

    const selectedTasks = useMemo(() => {
        const selectedIdSet = new Set(selectedTaskIds);
        return loadedTasks.filter((task) => selectedIdSet.has(task.id));
    }, [loadedTasks, selectedTaskIds]);

    const isBulkActionsFooterShown =
        selectedTaskIds.length > 0 && !selectedTaskId;

    const navigationTaskIds = useMemo(
        () =>
            (
                board.navigationTaskIds ??
                groupedTasks.flatMap((group: NestedTaskRow) =>
                    group.tasks
                        .filter((task) => !task.isNewPlaceholder)
                        .map((task) => task.id),
                )
            ).filter((taskId) => !isBusyClosesDemoId(taskId)),
        [board.navigationTaskIds, groupedTasks],
    );

    useEffect(() => {
        onNavigationTaskIdsChange(navigationTaskIds);
    }, [navigationTaskIds, onNavigationTaskIdsChange]);

    useEffect(() => {
        if (
            areClosesFetched &&
            !areClosesFetching &&
            !hasCloses &&
            overlayFilters.taskCloseIds?.length
        ) {
            onOverlayFiltersChange({ taskCloseIds: undefined });
        }
    }, [
        areClosesFetched,
        areClosesFetching,
        hasCloses,
        overlayFilters.taskCloseIds,
        onOverlayFiltersChange,
    ]);

    const taskAvailableFilters = useTaskAvailableFilters({
        hasCloses,
        workspaceTaskGroupIds,
        capabilities,
    });

    const viewToolbarContent = useMemo(
        () => (
            <ViewToolbar
                view={fullscreenViewForToolbar}
                onViewChange={handleFullscreenViewChange}
                onViewSelect={handleViewSelect}
                viewConfig={effectiveTaskViewConfig}
                builtInViews={builtInTaskViews}
                viewLabels={viewLabels}
                defaultFilters={DEFAULT_TASK_FILTERS}
                initialBaseViewId={landingViewId}
                isColumnSettingsHidden={isBoardLayout}
                viewCollection={viewCollection}
            >
                {!isBoardLayout && (
                    <ExpandAllButton expanded={expanded} handleRef={tableRef} />
                )}
                {toolbarControls}
            </ViewToolbar>
        ),
        [
            fullscreenViewForToolbar,
            handleFullscreenViewChange,
            handleViewSelect,
            effectiveTaskViewConfig,
            builtInTaskViews,
            viewLabels,
            landingViewId,
            isBoardLayout,
            viewCollection,
            toolbarControls,
            expanded,
        ],
    );

    useHideableSubNavigationToolbar({
        id: "tasks-view-toolbar",
        content: viewToolbarContent,
        isHidden: selectedTaskId !== null,
    });

    return (
        <>
            <TasksListHeader
                filters={filters}
                setFilters={setFilters}
                selectedTaskId={selectedTaskId}
                overlayFilters={overlayFilters}
                onOverlayFiltersChange={onOverlayFiltersChange}
                taskAvailableFilters={taskAvailableFilters}
                hasCloseStages={hasCloseStages}
                hasCloses={hasCloses}
                capabilities={capabilities}
            />
            {isBoardLayout ? (
                <TasksBoardContent
                    tasks={tasks}
                    board={board}
                    isCrossWorkspace={capabilities.isCrossWorkspace}
                    openTaskId={selectedTaskId}
                    onOpenTask={openTask}
                    onClearFilters={clearFilters}
                    notice={tableNotice}
                    viewEmptyState={viewEmptyState}
                />
            ) : (
                <TaskListOpenTaskContext.Provider value={openTask}>
                    <TaskListTable
                        ref={tableRef}
                        groups={tasks === null ? null : tableRows}
                        isRefetching={isShowingPreviousTasks}
                        taskLoadError={taskLoadError}
                        emptyState={emptyState}
                        config={effectiveTaskViewConfig}
                        view={listView}
                        onViewChange={handleListViewChange}
                        sort={sort}
                        onSortChange={changeSort}
                        expanded={expanded}
                        onExpandedChange={setExpanded}
                        activeTaskId={selectedTaskId}
                        onOpenTask={openTaskRow}
                        rowSelection={rowSelection}
                        onRowSelectionChange={setRowSelection}
                        canSelectRows={capabilities.canSelectRows}
                        isCrossWorkspace={capabilities.isCrossWorkspace}
                        serviceWorkspaceIds={serviceWorkspaceIds}
                        hasCloseStages={hasCloseStages}
                        notice={tableNotice}
                        footer={
                            capabilities.isCrossWorkspace ? (
                                <OrganizationTasksListBulkActions
                                    show={isBulkActionsFooterShown}
                                    selectedTaskIds={selectedTaskIds}
                                    selectedTasks={selectedTasks}
                                    clearSelection={clearSelection}
                                    setSelectedTaskIds={setSelectedTaskIds}
                                />
                            ) : (
                                <WorkspaceTasksListBulkActions
                                    show={isBulkActionsFooterShown}
                                    selectedTaskIds={selectedTaskIds}
                                    selectedTasks={selectedTasks}
                                    clearSelection={clearSelection}
                                />
                            )
                        }
                    />
                </TaskListOpenTaskContext.Provider>
            )}
        </>
    );
};
